/**
 * Centralized SEO Configuration for GoTurkeyTransfer
 * 
 * This file contains all SEO metadata for indexable pages.
 * URL Standard: WITH trailing slash (e.g., /istanbul-airport-transfer/)
 * This matches sitemap URLs and Nginx behavior.
 */

const SITE_URL = 'https://goturkeytransfer.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_2fc4c5d3-0206-4873-b905-9a56ef398b77/artifacts/7q9w4vy0_Go%20Turkey%20Transfer.png';

/**
 * URL Normalization Helper
 * - Adds trailing slash (for consistency with site standard)
 * - Converts to lowercase
 * - Strips tracking parameters
 */
export const normalizeUrl = (path) => {
  if (!path) return '/';
  
  // Remove query params
  let cleanPath = path.split('?')[0].split('#')[0];
  
  // Lowercase
  cleanPath = cleanPath.toLowerCase();
  
  // Ensure starts with /
  if (!cleanPath.startsWith('/')) {
    cleanPath = '/' + cleanPath;
  }
  
  // Add trailing slash for consistency (except for root which already has /)
  if (cleanPath !== '/' && !cleanPath.endsWith('/')) {
    cleanPath = cleanPath + '/';
  }
  
  return cleanPath;
};

/**
 * Get full canonical URL
 */
export const getCanonicalUrl = (path) => {
  const normalized = normalizeUrl(path);
  return `${SITE_URL}${normalized}`;
};

/**
 * Tracking parameters to strip from URLs
 */
export const TRACKING_PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'fbclid', 'msclkid', 'twclid', 'dclid',
  'ref', 'source', 'mc_cid', 'mc_eid'
];

/**
 * Pages that should NOT be indexed
 */
export const NOINDEX_PATHS = [
  '/quote',
  '/extras',
  '/details',
  '/payment',
  '/confirm',
  '/checkout',
  '/login-goturkeytransfer',
  '/admin',
  '/manage',
  '/feedback'
];

/**
 * Check if a path should be noindexed
 */
export const shouldNoIndex = (path) => {
  const normalized = normalizeUrl(path);
  return NOINDEX_PATHS.some(noIndexPath => 
    normalized === noIndexPath || normalized.startsWith(noIndexPath + '/')
  );
};

/**
 * SEO Page Configurations
 * Each page has: title, description, keywords, ogTitle, ogDescription, ogImage, twitterTitle, twitterDescription, robots, schema
 */
export const SEO_PAGES = {
  // Homepage
  '/': {
    title: 'Turkey Airport Transfers | Private, Fixed Price',
    description: 'Private airport transfers across Turkey: Istanbul, Antalya, Dalaman & Bodrum. Fixed prices, English-speaking drivers, free flight tracking, 24/7 support.',
    keywords: 'turkey airport transfer, private airport transfer turkey, istanbul airport transfer, antalya airport transfer, dalaman airport transfer, bodrum airport transfer',
    ogTitle: 'Turkey Airport Transfers | Private, Fixed Price – Go Turkey Transfer',
    ogDescription: 'Private airport transfers across Turkey: Istanbul, Antalya, Dalaman & Bodrum. Fixed prices, English-speaking drivers, free flight tracking, 24/7 support.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    hreflang: [
      { lang: 'en', url: `${SITE_URL}` },
      { lang: 'ru', url: `${SITE_URL}/ru` },
      { lang: 'x-default', url: `${SITE_URL}` }
    ],
    schema: ['organization', 'website', 'localBusiness', 'breadcrumb']
  },

  // Russian Homepage
  '/ru': {
    title: 'Трансфер из аэропортов Турции | Go Turkey Transfer',
    description: 'Частный трансфер из аэропортов Турции: Стамбул, Анталья, Даламан, Бодрум. Фиксированные цены, англоговорящие водители, отслеживание рейсов, 24/7.',
    keywords: 'трансфер турция, трансфер из аэропорта стамбул, трансфер анталия, трансфер даламан, трансфер бодрум',
    ogTitle: 'Трансфер из аэропортов Турции | Go Turkey Transfer',
    ogDescription: 'Частный трансфер из аэропортов Турции: Стамбул, Анталья, Даламан, Бодрум. Фиксированные цены, англоговорящие водители, отслеживание рейсов, 24/7.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    ogLocale: 'ru_RU',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    hreflang: [
      { lang: 'en', url: `${SITE_URL}` },
      { lang: 'ru', url: `${SITE_URL}/ru` },
      { lang: 'x-default', url: `${SITE_URL}` }
    ],
    schema: ['organization', 'website', 'breadcrumb']
  },

  // Istanbul Airport Transfer
  '/istanbul-airport-transfer': {
    title: 'Istanbul Airport Transfer (IST & SAW) | Fixed Price',
    description: 'Private Istanbul Airport transfers from IST & SAW to Taksim, Sultanahmet & all hotels. Fixed price, meet & greet, flight tracking. Book online in minutes.',
    keywords: 'istanbul airport transfer, ist airport transfer, sabiha gokcen transfer, istanbul airport taxi, private transfer istanbul',
    ogTitle: 'Istanbul Airport Transfer (IST & SAW) | Fixed Price Private Taxi',
    ogDescription: 'Private Istanbul Airport transfers from IST & SAW to Taksim, Sultanahmet & all hotels. Fixed price, meet & greet, flight tracking. Book online in minutes.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb', 'faq']
  },

  // Antalya Airport Transfer
  '/antalya-airport-transfer': {
    title: 'Antalya Airport Transfer (AYT) | All Resorts',
    description: 'Book your Antalya Airport (AYT) transfer to Belek, Side, Kemer, Lara & Alanya. Fixed-price private cars, English-speaking drivers, 24/7 meet & greet.',
    keywords: 'antalya airport transfer, ayt airport transfer, antalya to kemer transfer, antalya to alanya transfer, antalya to belek transfer, antalya to side transfer',
    ogTitle: 'Antalya Airport Transfer (AYT) | Private Transfer to All Resorts',
    ogDescription: 'Book your Antalya Airport (AYT) transfer to Belek, Side, Kemer, Lara & Alanya. Fixed-price private cars, English-speaking drivers, 24/7 meet & greet.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Dalaman Airport Transfer
  '/dalaman-airport-transfer': {
    title: 'Dalaman Airport Transfer (DLM) | Fethiye, Marmaris, Ölüdeniz',
    description: 'Private Dalaman Airport transfers to Fethiye, Ölüdeniz, Marmaris, Kalkan & Kaş. Fixed prices, child seats, free flight monitoring. Reserve online now.',
    keywords: 'dalaman airport transfer, dlm airport transfer, dalaman to fethiye transfer, dalaman to marmaris transfer, dalaman to oludeniz',
    ogTitle: 'Dalaman Airport Transfer (DLM) | Fethiye, Marmaris, Ölüdeniz',
    ogDescription: 'Private Dalaman Airport transfers to Fethiye, Ölüdeniz, Marmaris, Kalkan & Kaş. Fixed prices, child seats, free flight monitoring. Reserve online now.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Bodrum Airport Transfer
  '/bodrum-airport-transfer': {
    title: 'Bodrum Airport Transfer (BJV) | Private Peninsula',
    description: 'Bodrum Airport (BJV) transfers to Bodrum, Turgutreis, Yalıkavak & Gümbet. Fixed-price private vehicles, English-speaking drivers, 24/7 support.',
    keywords: 'bodrum airport transfer, bjv airport transfer, bodrum milas airport, bodrum to yalikavak, bodrum to turgutreis',
    ogTitle: 'Bodrum Airport Transfer (BJV) | Private Taxi to the Peninsula',
    ogDescription: 'Bodrum Airport (BJV) transfers to Bodrum, Turgutreis, Yalıkavak & Gümbet. Fixed-price private vehicles, English-speaking drivers, 24/7 support.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Antalya Airport to Side Transfer
  '/antalya-airport-to-side-transfer': {
    title: 'Antalya Airport to Side Transfer | Fixed Price (~65 min)',
    description: 'Private transfer from Antalya Airport (AYT) to Side hotels. ~65 min drive, fixed price, meet & greet, flight tracking. Book your Side transfer online.',
    keywords: 'antalya airport to side transfer, side airport transfer, antalya to side private taxi, side hotel transfer',
    ogTitle: 'Antalya Airport to Side Transfer | Fixed Price (~65 min)',
    ogDescription: 'Private transfer from Antalya Airport (AYT) to Side hotels. ~65 min drive, fixed price, meet & greet, flight tracking. Book your Side transfer online.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Antalya Airport to Belek Transfer
  '/antalya-airport-to-belek-transfer': {
    title: 'Antalya Airport to Belek Transfer | Private, Fixed Price',
    description: 'Private Antalya Airport to Belek resort transfer. ~40 min, fixed price, English-speaking driver, child seats available. Book online 24/7.',
    keywords: 'antalya airport to belek transfer, belek airport transfer, antalya to belek private taxi, belek resort transfer',
    ogTitle: 'Antalya Airport to Belek Transfer | Private, Fixed Price',
    ogDescription: 'Private Antalya Airport to Belek resort transfer. ~40 min, fixed price, English-speaking driver, child seats available. Book online 24/7.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Antalya Airport to Kemer Transfer
  '/antalya-airport-to-kemer-transfer': {
    title: 'Antalya Airport to Kemer Transfer | Private Taxi Service',
    description: 'Antalya Airport (AYT) to Kemer private transfer. Fixed price, ~50 min scenic drive, meet & greet, free flight tracking. Reserve in minutes.',
    keywords: 'antalya airport to kemer transfer, kemer airport transfer, antalya to kemer private taxi, kemer hotel transfer',
    ogTitle: 'Antalya Airport to Kemer Transfer | Private Taxi Service',
    ogDescription: 'Antalya Airport (AYT) to Kemer private transfer. Fixed price, ~50 min scenic drive, meet & greet, free flight tracking. Reserve in minutes.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Antalya Airport to Alanya Transfer
  '/antalya-airport-to-alanya-transfer': {
    title: 'Antalya Airport to Alanya Transfer | Fixed Price (~2 hrs)',
    description: 'Private Antalya Airport to Alanya transfer, ~2 hours. Fixed price, comfortable vehicles, English-speaking drivers, 24/7 meet & greet. Book now.',
    keywords: 'antalya airport to alanya transfer, alanya airport transfer, antalya to alanya private taxi, alanya hotel transfer',
    ogTitle: 'Antalya Airport to Alanya Transfer | Fixed Price (~2 hrs)',
    ogDescription: 'Private Antalya Airport to Alanya transfer, ~2 hours. Fixed price, comfortable vehicles, English-speaking drivers, 24/7 meet & greet. Book now.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Dalaman Airport to Fethiye Transfer
  '/dalaman-airport-to-fethiye-transfer': {
    title: 'Dalaman Airport to Fethiye Transfer | Fixed Price (~50 min)',
    description: 'Private Dalaman Airport to Fethiye transfer in ~50 min. Fixed price, child seats, free flight monitoring, meet & greet. Book your Fethiye transfer.',
    keywords: 'dalaman airport to fethiye transfer, fethiye airport transfer, dalaman to fethiye private taxi, fethiye hotel transfer',
    ogTitle: 'Dalaman Airport to Fethiye Transfer | Fixed Price (~50 min)',
    ogDescription: 'Private Dalaman Airport to Fethiye transfer in ~50 min. Fixed price, child seats, free flight monitoring, meet & greet. Book your Fethiye transfer.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Dalaman Airport to Oludeniz Transfer
  '/dalaman-airport-to-oludeniz-transfer': {
    title: 'Dalaman Airport to Ölüdeniz Transfer | Private, Fixed Price',
    description: 'Dalaman Airport to Ölüdeniz private transfer, ~1 hour. Fixed price, English-speaking drivers, child seats, 24/7 service. Reserve online today.',
    keywords: 'dalaman airport to oludeniz transfer, oludeniz airport transfer, dalaman to oludeniz private taxi, oludeniz hotel transfer',
    ogTitle: 'Dalaman Airport to Ölüdeniz Transfer | Private, Fixed Price',
    ogDescription: 'Dalaman Airport to Ölüdeniz private transfer, ~1 hour. Fixed price, English-speaking drivers, child seats, 24/7 service. Reserve online today.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Dalaman Airport to Marmaris Transfer
  '/dalaman-airport-to-marmaris-transfer': {
    title: 'Dalaman Airport to Marmaris Transfer | Fixed Price (~90 min)',
    description: 'Private Dalaman Airport to Marmaris transfer, ~90 min. Fixed price, no hidden fees, flight tracking, meet & greet. Book your Marmaris transfer online.',
    keywords: 'dalaman airport to marmaris transfer, marmaris airport transfer, dalaman to marmaris private taxi, marmaris hotel transfer',
    ogTitle: 'Dalaman Airport to Marmaris Transfer | Fixed Price (~90 min)',
    ogDescription: 'Private Dalaman Airport to Marmaris transfer, ~90 min. Fixed price, no hidden fees, flight tracking, meet & greet. Book your Marmaris transfer online.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Bodrum Airport to Turgutreis Transfer
  '/bodrum-airport-to-turgutreis-transfer': {
    title: 'Bodrum Airport to Turgutreis Transfer | Private Taxi',
    description: 'Bodrum Airport (BJV) to Turgutreis private transfer. Fixed price, ~45 min, English-speaking driver, free flight tracking. Book online 24/7.',
    keywords: 'bodrum airport to turgutreis transfer, turgutreis airport transfer, bodrum to turgutreis private taxi',
    ogTitle: 'Bodrum Airport to Turgutreis Transfer | Private Taxi',
    ogDescription: 'Bodrum Airport (BJV) to Turgutreis private transfer. Fixed price, ~45 min, English-speaking driver, free flight tracking. Book online 24/7.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Bodrum Airport to Yalikavak Transfer
  '/bodrum-airport-to-yalikavak-transfer': {
    title: 'Bodrum Airport to Yalıkavak Transfer | Marina & Villas',
    description: 'Private Bodrum Airport to Yalıkavak transfer (marina & villas). Fixed price, ~45 min, premium vehicles, meet & greet. Reserve your transfer now.',
    keywords: 'bodrum airport to yalikavak transfer, yalikavak airport transfer, bodrum to yalikavak private taxi, yalikavak marina transfer',
    ogTitle: 'Bodrum Airport to Yalıkavak Transfer | Marina & Villas',
    ogDescription: 'Private Bodrum Airport to Yalıkavak transfer (marina & villas). Fixed price, ~45 min, premium vehicles, meet & greet. Reserve your transfer now.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Medical Tourism
  '/medical-tourism-turkey-vip-transfers': {
    title: 'Turkey Medical Tourism Transfers | Clinic VIP Transfers',
    description: 'VIP airport-to-clinic transfers for medical tourists in Turkey: hair transplant, dental & aesthetic patients. Discreet, English-speaking drivers.',
    keywords: 'medical tourism turkey, hospital transfer turkey, clinic transfer istanbul, medical travel turkey, health tourism transfer, hair transplant transfer',
    ogTitle: 'Turkey Medical Tourism Transfers | Clinic VIP Transfers',
    ogDescription: 'VIP airport-to-clinic transfers for medical tourists in Turkey: hair transplant, dental & aesthetic patients. Discreet, comfortable, English-speaking drivers.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Antalya Airport Transfer Guide
  '/antalya-airport-transfer-guide': {
    title: 'Antalya Airport Transfer Guide | Routes, Times & Prices',
    description: 'Complete Antalya Airport (AYT) transfer guide: drive times, distances and prices to Belek, Side, Kemer, Lara & Alanya. Plan your transfer with confidence.',
    keywords: 'antalya airport transfer guide, antalya airport routes, antalya transfer times, antalya transfer prices, ayt airport guide',
    ogTitle: 'Antalya Airport Transfer Guide | Routes, Times & Prices',
    ogDescription: 'Complete Antalya Airport (AYT) transfer guide: drive times, distances and prices to Belek, Side, Kemer, Lara & Alanya. Plan your transfer with confidence.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'article',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'article', 'breadcrumb']
  },

  // VIP Transfer Services
  '/turkey-vip-transfer-services': {
    title: 'VIP Transfer Services Turkey | Premium Airport Transfers',
    description: 'Premium VIP transfer services across Turkey. Luxury vehicles, professional chauffeurs, free meet & greet, free cancellation.',
    keywords: 'vip transfer turkey, luxury transfer turkey, premium airport transfer, chauffeur service turkey, executive transfer',
    ogTitle: 'VIP Transfer Services Turkey | Premium Airport Transfers',
    ogDescription: 'Premium VIP transfer services across Turkey. Luxury vehicles, professional chauffeurs, free meet & greet, free cancellation.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // Corporate Travel
  '/corporate-travel': {
    title: 'Corporate Travel Turkey | Business Airport Transfers',
    description: 'Professional corporate transfer services in Turkey. Executive vehicles, punctual service, billing solutions for businesses.',
    keywords: 'corporate transfer turkey, business airport transfer, executive transfer istanbul, corporate travel services',
    ogTitle: 'Corporate Travel Turkey | Business Airport Transfers',
    ogDescription: 'Professional corporate transfer services for businesses in Turkey.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'service', 'breadcrumb']
  },

  // About
  '/about': {
    title: 'About Go Turkey Transfer | Private Transfers Across Turkey',
    description: 'Learn about Go Turkey Transfer — private, fixed-price airport transfers across Turkey with English-speaking drivers and 24/7 support since launch.',
    keywords: 'about go turkey transfer, airport transfer company turkey, private transfer turkey',
    ogTitle: 'About Go Turkey Transfer | Private Transfers Across Turkey',
    ogDescription: 'Learn about Go Turkey Transfer — private, fixed-price airport transfers across Turkey with English-speaking drivers and 24/7 support since launch.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'breadcrumb']
  },

  // Contact
  '/contact': {
    title: 'Contact Go Turkey Transfer | 24/7 Support & WhatsApp',
    description: 'Contact Go Turkey Transfer for bookings and support. WhatsApp, phone and email available 24/7. We respond fast to all transfer enquiries.',
    keywords: 'contact go turkey transfer, airport transfer support, customer service turkey, whatsapp transfer booking',
    ogTitle: 'Contact Go Turkey Transfer | 24/7 Support & WhatsApp',
    ogDescription: 'Contact Go Turkey Transfer for bookings and support. WhatsApp, phone and email available 24/7. We respond fast to all transfer enquiries.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'breadcrumb']
  },

  // FAQ
  '/faq': {
    title: 'Airport Transfer FAQ | Go Turkey Transfer',
    description: 'Answers to common questions about Turkey airport transfers: pricing, flight delays, cancellations, child seats, meeting points and payment.',
    keywords: 'airport transfer faq, turkey transfer questions, booking help, cancellation policy, flight delay transfer',
    ogTitle: 'Airport Transfer FAQ | Go Turkey Transfer',
    ogDescription: 'Answers to common questions about Turkey airport transfers: pricing, flight delays, cancellations, child seats, meeting points and payment.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'faqPage', 'breadcrumb']
  },

  // Blog
  '/blog': {
    title: 'Turkey Travel & Transfer Blog | Go Turkey Transfer',
    description: 'Guides and tips for travelling in Turkey: airport transfers, routes, resorts and what to know before you arrive. By Go Turkey Transfer.',
    keywords: 'turkey travel blog, istanbul travel tips, antalya guide, turkey airport transfer tips, turkish tourism',
    ogTitle: 'Turkey Travel & Transfer Blog | Go Turkey Transfer',
    ogDescription: 'Guides and tips for travelling in Turkey: airport transfers, routes, resorts and what to know before you arrive. By Go Turkey Transfer.',
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'blog',
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
    schema: ['organization', 'blog', 'breadcrumb']
  },

  // Cancellation & Refund Policy
  '/cancellation-refund-policy': {
    title: 'Cancellation & Refund Policy | Go Turkey Transfer',
    description: 'Read our cancellation and refund policy. Free cancellation up to 24 hours before your transfer. Clear, fair terms with no hidden fees.',
    robots: 'index, follow',
    schema: ['organization', 'breadcrumb']
  },

  // Cookie Policy
  '/cookie-policy': {
    title: 'Cookie Policy | Go Turkey Transfer',
    description: 'How Go Turkey Transfer uses cookies. Learn about the cookies we use and how to manage your preferences.',
    robots: 'index, follow',
    schema: ['organization', 'breadcrumb']
  },

  // Legal Pages
  '/terms-of-service': {
    title: 'Terms of Service | Go Turkey Transfer',
    description: 'Read the terms of service for Go Turkey Transfer airport transfer bookings. Understand your rights and responsibilities.',
    robots: 'index, follow',
    schema: ['organization', 'breadcrumb']
  },

  '/privacy-policy': {
    title: 'Privacy Policy | Go Turkey Transfer',
    description: 'Learn how Go Turkey Transfer protects your personal data. Our privacy policy explains data collection, use, and your rights.',
    robots: 'index, follow',
    schema: ['organization', 'breadcrumb']
  }
};

/**
 * Get SEO config for a specific path
 * Falls back to default config if path not found
 */
export const getSEOConfig = (path) => {
  const normalized = normalizeUrl(path);
  
  // Try with trailing slash
  if (SEO_PAGES[normalized]) {
    return {
      ...SEO_PAGES[normalized],
      canonical: getCanonicalUrl(normalized)
    };
  }
  
  // Try without trailing slash (for key matching)
  const withoutSlash = normalized.endsWith('/') && normalized !== '/' 
    ? normalized.slice(0, -1) 
    : normalized;
  
  if (SEO_PAGES[withoutSlash]) {
    return {
      ...SEO_PAGES[withoutSlash],
      canonical: getCanonicalUrl(normalized)
    };
  }
  
  // Check for blog detail page pattern
  if (normalized.startsWith('/blog/') && normalized !== '/blog/') {
    return {
      // Blog detail pages get config from CMS
      canonical: getCanonicalUrl(normalized),
      robots: 'index, follow',
      schema: ['organization', 'blogPosting', 'breadcrumb']
    };
  }
  
  // Default fallback
  return {
    title: 'GoTurkeyTransfer | Premium Airport Transfers in Turkey',
    description: 'Book premium airport transfers in Turkey with GoTurkeyTransfer.',
    canonical: getCanonicalUrl(normalized),
    robots: shouldNoIndex(normalized) ? 'noindex, nofollow' : 'index, follow',
    schema: ['organization']
  };
};

/**
 * Generate breadcrumb items for a path
 */
export const getBreadcrumbItems = (path, pageTitle) => {
  const normalized = normalizeUrl(path);
  const items = [
    { name: 'Home', url: SITE_URL }
  ];
  
  if (normalized === '/') {
    return items;
  }
  
  // Parse path segments
  const segments = normalized.split('/').filter(Boolean);
  let currentPath = '';
  
  segments.forEach((segment, index) => {
    currentPath += '/' + segment;
    const isLast = index === segments.length - 1;
    
    // Generate human-readable name
    let name = segment
      .replace(/-/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    
    // Use page title for last segment if available
    if (isLast && pageTitle) {
      name = pageTitle;
    }
    
    items.push({
      name,
      url: isLast ? getCanonicalUrl(currentPath) : getCanonicalUrl(currentPath)
    });
  });
  
  return items;
};

// Export constants
export { SITE_URL, DEFAULT_OG_IMAGE, LOGO_URL };
